export const planDuration = '2027/28-2031/32';

/**
 * MTREF budget years in positional order. The API stores positional keys only,
 * so `label` and `offset` exist purely for display. Following National Treasury
 * convention, the first year is the budget year and the outer two are expressed
 * as offsets from it.
 */
export const mtrefYears = [
  { key: 'year1', label: '2027/28', offset: 'Year' },
  { key: 'year2', label: '2028/29', offset: 'Year +1' },
  { key: 'year3', label: '2029/30', offset: 'Year +2' },
];

/**
 * Column headings for the financial plan budget tables and the project upload
 * template. The R'000 unit is repeated on every column so a heading still reads
 * correctly when the table breaks across PDF pages.
 */
export const mtrefYearHeadings = mtrefYears.map(year => `${year.offset} ${year.label} (R'000)`);

export const MunicipalityType = {
  Municipality: 'Local Municipality',
  DistrictMunicipality: 'District Municipality',
};

export const Role = {
  'SystemAdministrator': 'System Administrator',
  'MunicipalAdministrator': 'Municipal Administrator',
  'IdpOfficer': 'IDP Officer',
  'Viewer': 'Viewer',
};

export const PlanStatus = {
  Published: 'Published',
  Draft: 'Draft',
};

export const PlanWorkflowStep = {
  WorkInProgress1: 'work_in_progress_1',
  SubmittedForCouncilNoting: 'submitted_for_council_noting',
  SubmittedForMecReview: 'submitted_for_mec_review',
  WorkInProgress2: 'work_in_progress_2',
  DraftApproval: 'draft_approval',
  WorkInProgress3: 'work_in_progress_3',
  SubmittedForCouncilApproval: 'submitted_for_council_approval',
  SubmittedToMec: 'submitted_to_mec',
  FinalApproval: 'final_approval',
  PublishedAsFinal: 'published_as_final',
};

export const PlanWorkflowStepOrder = Object.values(PlanWorkflowStep);

export const PlanOfficials = {
  MunicipalManager: 'municipalManager',
  Mayor: 'mayor',
};

/**
 * Display names for the plan's content sections, keyed by the content keys the API reports in
 * `blankSections`. The names match the editor navigation.
 */
export const PlanSectionName = {
  preliminaries: 'Preliminaries',
  executiveSummary: 'Executive Summary',
  introduction: 'Introduction',
  vision: 'Vision, Mission, Values, Goals and Objectives',
  situationalAnalysis: 'Situational Analysis',
  institutionalFramework: 'Institutional Framework',
  spatialDevelopmentFramework: 'Spatial Development Framework',
  developmentStrategies: 'Development Strategies',
  implementationPlan: 'Implementation Plan',
  financialPlan: 'Financial Plan',
  keyPerformanceIndicators: 'Performance Monitoring Framework',
};

export const rowDefinitions = {
  financialPerformanceProjection: [
    { label: 'Financial Performance', type: 'underlined-header' },
    { label: 'Property rates', type: 'value', path: 'revenue.propertyRates', format: 'currency' },
    { label: 'Service charges', type: 'value', path: 'revenue.serviceCharges', format: 'currency' },
    { label: 'Investment revenue', type: 'value', path: 'revenue.investmentRevenue', format: 'currency' },
    { label: 'Transfers and subsidies - Operational', type: 'value', path: 'revenue.operationalTransfersAndSubsidies', format: 'currency' },
    { label: 'Other own revenue', type: 'value', path: 'revenue.otherOwnRevenue', format: 'currency' },

    { label: 'Total Revenue (excluding capital transfers and contributions)', type: 'total', path: 'totalRevenue', format: 'currency' },

    { label: 'Employee costs', type: 'value', path: 'expenditure.employeeCosts', format: 'currency' },
    { label: 'Remuneration of councillors', type: 'value', path: 'expenditure.councillorRemuneration', format: 'currency' },
    { label: 'Depreciation and amortisation', type: 'value', path: 'expenditure.depreciationAndAmortisation', format: 'currency' },
    { label: 'Finance charges', type: 'value', path: 'expenditure.financeCharges', format: 'currency' },
    { label: 'Inventory consumed and bulk purchases', type: 'value', path: 'expenditure.inventoryConsumed', format: 'currency' },
    { label: 'Transfers and subsidies', type: 'value', path: 'expenditure.transfersAndSubsidies', format: 'currency' },
    { label: 'Other expenditure', type: 'value', path: 'expenditure.other', format: 'currency' },

    { label: 'Total Expenditure', type: 'total', path: 'totalExpenditure', format: 'currency' },

    { label: 'Surplus/(Deficit)', type: 'borderless-total', path: 'surplusDeficit', format: 'currency' },

    { label: 'Transfers and subsidies - capital (monetary allocations)', type: 'value', path: 'monetaryTransfersAndSubsidies', format: 'currency' },
    { label: 'Transfers and subsidies - capital (in-kind)', type: 'value', path: 'inkindTransfersAndSubsidies', format: 'currency' },

    { label: 'Surplus/(Deficit) after capital transfers & contributions', type: 'total', path: 'surplusDeficitAfterCapital', format: 'currency' },

    { label: 'Share of Surplus/deficit attributable to Associate', type: 'value', path: 'attributableShare', format: 'currency' },
    { label: 'Surplus/(Deficit) for the year', type: 'total', path: 'surplusDeficitForYear', format: 'currency' },
  ],
  capitalExpenditureAndFinancing: [
    { label: 'Capital Expenditure - Functional', type: 'underlined-header' },

    { label: 'Municipal governance and administration\n ', type: 'italic-total', path: 'subTotalGov', format: 'currency' },
    { label: 'Executive and council', type: 'value', path: 'municipalGovernanceAndAdministration.executiveAndCouncil', format: 'currency' },
    { label: 'Finance and administration', type: 'value', path: 'municipalGovernanceAndAdministration.financeAndAdministration', format: 'currency' },
    { label: 'Internal audit', type: 'value', path: 'municipalGovernanceAndAdministration.internalAudit', format: 'currency' },

    { label: 'Total Community and public safety', type: 'italic-total', path: 'subTotalCommunity', format: 'currency' },
    { label: 'Community and social services', type: 'value', path: 'communityAndPublicSafety.communityAndSocialServices', format: 'currency' },
    { label: 'Sport and recreation', type: 'value', path: 'communityAndPublicSafety.sportAndRecreation', format: 'currency' },
    { label: 'Public safety', type: 'value', path: 'communityAndPublicSafety.publicSafety', format: 'currency' },
    { label: 'Housing', type: 'value', path: 'communityAndPublicSafety.housing', format: 'currency' },
    { label: 'Health', type: 'value', path: 'communityAndPublicSafety.health', format: 'currency' },

    { label: '\nEconomic and environmental services', type: 'italic-total', path: 'subTotalEconomic', format: 'currency' },
    { label: 'Planning and development', type: 'value', path: 'economicAndEnvironmentalServices.planningAndDevelopment', format: 'currency' },
    { label: 'Road transport', type: 'value', path: 'economicAndEnvironmentalServices.roadTransport', format: 'currency' },
    { label: 'Environmental protection', type: 'value', path: 'economicAndEnvironmentalServices.environmentalProtection', format: 'currency' },

    { label: '\nTrading services', type: 'italic-total', path: 'subTotalTrading', format: 'currency' },
    { label: 'Energy sources', type: 'value', path: 'tradingServices.energySources', format: 'currency' },
    { label: 'Water management', type: 'value', path: 'tradingServices.waterAndManagement', format: 'currency' },
    { label: 'Waste water management', type: 'value', path: 'tradingServices.wasteWaterManagement', format: 'currency' },
    { label: 'Waste management', type: 'value', path: 'tradingServices.wasteManagement', format: 'currency' },

    { label: '\nOther', type: 'bold-italic-value', path: 'other', format: 'currency' },
    { label: '\nTotal Capital Expenditure - Functional', type: 'total', path: 'totalCapitalExpenditure', format: 'currency' },

    { type: 'empty' },

    { label: 'Funded By', type: 'underlined-header' },
    { label: 'National Government', type: 'value', path: 'funding.nationalGovernment', format: 'currency' },
    { label: 'Provincial Government', type: 'value', path: 'funding.provincialGovernment', format: 'currency' },
    { label: 'District Municipality', type: 'value', path: 'funding.districtMunicipality', format: 'currency' },
    { label: 'Transfers and subsidies - capital (monetary allocations) ', type: 'value', path: 'funding.transfersAndSubsidies', format: 'currency' },
    { label: '\nTransfers recognised - capital', type: 'total', path: 'totalCapitalTransfers', format: 'currency' },

    { type: 'empty' },

    { label: 'Borrowing', type: 'no-indent-italic-value', path: 'borrowing', format: 'currency' },
    { label: 'Internally generated funds', type: 'no-indent-italic-value', path: 'internallyGeneratedFunds', format: 'currency' },
    { label: 'Total Capital Funding\n ', type: 'total', path: 'totalCapitalFunding', format: 'currency' },
  ],
  financialPositionProjection: [
    { label: 'Assets', type: 'uppercase-header' },
    { label: 'Current assets\n ', type: 'underlined-header' },

    { label: 'Cash and cash equivalents', type: 'value', path: 'currentAssets.cash', format: 'currency' },
    { label: 'Short term Investments', type: 'value', path: 'currentAssets.shortTermInvestments', format: 'currency' },
    { label: 'Trade and other receivables from exchange transactions', type: 'value', path: 'currentAssets.exchangeTransactions', format: 'currency' },
    { label: 'Receivables from non-exchange transactions', type: 'value', path: 'currentAssets.nonexchangeTransactions', format: 'currency' },
    { label: 'Current portion of non-current receivables', type: 'value', path: 'currentAssets.nonCurrentReceivables', format: 'currency' },
    { label: 'Inventory', type: 'value', path: 'currentAssets.inventory', format: 'currency' },
    { label: 'VAT Receivable', type: 'value', path: 'currentAssets.vat', format: 'currency' },
    { label: 'Other current assets', type: 'value', path: 'currentAssets.other', format: 'currency' },

    { label: '\nTotal Current Assets', type: 'total', path: 'totalCurrentAssets', format: 'currency' },

    { label: 'Non current assets', type: 'underlined-header' },

    { label: 'Investments', type: 'value', path: 'noncurrentAssets.investments', format: 'currency' },
    { label: 'Investment property', type: 'value', path: 'noncurrentAssets.investmentProperty', format: 'currency' },
    { label: 'Property, plant and equipment', type: 'value', path: 'noncurrentAssets.propertyPlantAndEquipment', format: 'currency' },
    { label: 'Biological assets', type: 'value', path: 'noncurrentAssets.biologicalAssets', format: 'currency' },
    { label: 'Living and non-living resources', type: 'value', path: 'noncurrentAssets.resources', format: 'currency' },
    { label: 'Heritage assets', type: 'value', path: 'noncurrentAssets.heritageAssets', format: 'currency' },
    { label: 'Intangible assets', type: 'value', path: 'noncurrentAssets.intangibleAssets', format: 'currency' },
    { label: 'Trade and other receivables from exchange transactions', type: 'value', path: 'noncurrentAssets.exchangeTransactions', format: 'currency' },
    { label: 'Non-current receivables from non-exchange transactions', type: 'value', path: 'noncurrentAssets.nonexchangeTransactions', format: 'currency' },
    { label: 'Other non-current assets', type: 'value', path: 'noncurrentAssets.other', format: 'currency' },
    { label: '\nTotal non current assets', type: 'total', path: 'totalNonCurrentAssets', format: 'currency' },
    { label: '\nTotal Assets', type: 'uppercase-total', path: 'totalAssets', format: 'currency' },

    { label: 'Liabilities', type: 'uppercase-header' },
    { label: 'Current liabilities', type: 'underlined-header' },

    { label: 'Bank overdraft', type: 'value', path: 'currentLiabilities.bankOverdraft', format: 'currency' },
    { label: 'Financial liabilities', type: 'value', path: 'currentLiabilities.financialLiabilities', format: 'currency' },
    { label: 'Consumer deposits', type: 'value', path: 'currentLiabilities.consumerDeposits', format: 'currency' },
    { label: 'Trade and other payables from exchange transactions', type: 'value', path: 'currentLiabilities.exchangeTransactions', format: 'currency' },
    { label: 'Trade and other payables from non-exchange transactions', type: 'value', path: 'currentLiabilities.nonexchangeTransactions', format: 'currency' },
    { label: 'Provision', type: 'value', path: 'currentLiabilities.provision', format: 'currency' },
    { label: 'VAT Payable', type: 'value', path: 'currentLiabilities.vat', format: 'currency' },
    { label: 'Other current liabilities', type: 'value', path: 'currentLiabilities.other', format: 'currency' },

    { label: '\nTotal current liabilities', type: 'total', path: 'totalCurrentLiabilities', format: 'currency' },

    { label: 'Non current liabilities', type: 'subheader' },
    { label: 'Financial liabilities', type: 'value', path: 'noncurrentLiabilities.financialLiabilities', format: 'currency' },
    { label: 'Provision', type: 'value', path: 'noncurrentLiabilities.provision', format: 'currency' },
    { label: 'Long term portion of trade payables', type: 'value', path: 'noncurrentLiabilities.longTermTradePayables', format: 'currency' },
    { label: 'Other non-current liabilities', type: 'value', path: 'noncurrentLiabilities.other', format: 'currency' },

    { label: '\nTotal non current liabilities', type: 'total', path: 'totalNonCurrentLiabilities', format: 'currency' },

    { label: '\nTotal Liabilities', type: 'uppercase-total', path: 'totalLiabilities', format: 'currency' },
    { label: '\nNet Assets', type: 'uppercase-total', path: 'netAssets', format: 'currency' },

    { label: 'Community Wealth/Equity', type: 'uppercase-header' },

    { label: 'Accumulated surplus/(deficit)', type: 'value', path: 'equity.accumulatedSurplusOrDeficit', format: 'currency' },
    { label: 'Reserves and funds', type: 'value', path: 'equity.reservesAndFunds', format: 'currency' },
    { label: 'Other', type: 'value', path: 'equity.other', format: 'currency' },

    { label: '\nTotal Community Wealth/Equity', type: 'uppercase-total', path: 'totalCommunityWealth', format: 'currency' },
  ],
  cashFlowProjection: [
    { label: 'Cash Flow From Operating Activities', type: 'uppercase-header' },
    { label: 'Receipts', type: 'subheader' },
    { label: 'Property rates', type: 'value', path: 'operatingActivities.receipts.propertyRates', format: 'currency' },
    { label: 'Service charges', type: 'value', path: 'operatingActivities.receipts.serviceCharges', format: 'currency' },
    { label: 'Other revenue', type: 'value', path: 'operatingActivities.receipts.otherRevenue', format: 'currency' },
    { label: 'Transfers and Subsidies - Operational', type: 'value', path: 'operatingActivities.receipts.operationalTransfersAndSubsidies', format: 'currency' },
    { label: 'Transfers and Subsidies - Capital', type: 'value', path: 'operatingActivities.receipts.capitalTransfersAndSubsidies', format: 'currency' },
    { label: 'Interest', type: 'value', path: 'operatingActivities.receipts.interest', format: 'currency' },
    { label: 'Dividends', type: 'value', path: 'operatingActivities.receipts.dividends', format: 'currency' },

    { label: 'Payments', type: 'subheader' },
    { label: 'Suppliers and employees', type: 'value', path: 'operatingActivities.payments.suppliersAndEmployees', format: 'currency' },
    { label: 'Finance charges', type: 'value', path: 'operatingActivities.payments.financeCharges', format: 'currency' },
    { label: 'Transfers and subsidies', type: 'value', path: 'operatingActivities.payments.transfersAndSubsidies', format: 'currency' },

    { label: '\nNet cash from/(used) operating activities', type: 'uppercase-total', path: 'netCashOperating', format: 'currency' },

    { type: 'empty' },

    { label: 'Cash flows from investing activities', type: 'uppercase-header' },

    { label: 'Receipts', type: 'subheader' },
    { label: 'Proceeds on disposal of PPE', type: 'value', path: 'investingActivities.receipts.ppeDisposalProceeds', format: 'currency' },
    { label: 'Decrease (increase) in non-current debtors (not used)', type: 'value', path: 'investingActivities.receipts.noncurrentDebtors', format: 'currency' },
    { label: 'Decrease (increase) in non-current receivables', type: 'value', path: 'investingActivities.receipts.noncurrentReceivables', format: 'currency' },
    { label: 'Decrease (increase) in non-current investments', type: 'value', path: 'investingActivities.receipts.noncurrentInvestments', format: 'currency' },
    { label: 'Insurance Refund - Capital', type: 'value', path: 'investingActivities.receipts.insuranceRefundCapital', format: 'currency' },
    { label: 'Interest on Short Term Investment (Greater then 90 days) and Long Term Investments', type: 'value', path: 'investingActivities.receipts.shortLongTermInvestmentInterest', format: 'currency' },

    { label: 'Payments', type: 'subheader' },
    { label: 'Capital assets', type: 'value', path: 'investingActivities.payments.capitalAssets', format: 'currency' },
    { label: 'Retention (Capital)', type: 'value', path: 'investingActivities.payments.retentionCapital', format: 'currency' },
    { label: '\nNet cash from/(used) investing activities', type: 'uppercase-total', path: 'netCashInvesting', format: 'currency' },

    { type: 'empty' },

    { label: 'Cash flows from financing activities', type: 'uppercase-header' },

    { label: 'Receipts', type: 'subheader' },
    { label: 'Short term loans', type: 'value', path: 'financingActivities.receipts.shortTermLoans', format: 'currency' },
    { label: 'Borrowing long term/refinancing', type: 'value', path: 'financingActivities.receipts.longTermBorrowing', format: 'currency' },
    { label: 'Increase (decrease) in consumer deposits', type: 'value', path: 'financingActivities.receipts.consumerDeposits', format: 'currency' },

    { label: 'Payments', type: 'subheader' },
    { label: 'Repayment of borrowing', type: 'value', path: 'financingActivities.payments.borrowingRepayment', format: 'currency' },

    { label: '\nNet cash from/(used) financing activities', type: 'uppercase-total', path: 'netCashFinancing', format: 'currency' },
    { label: 'Net increase/(decrease) in cash held', type: 'uppercase-total', path: 'netIncreaseDecrease', format: 'currency' },

    { type: 'empty' },

    { label: 'Cash/cash equivalents at the year begin:', type: 'bold-total', path: 'yearBeginCash', format: 'currency' },
    { label: 'Cash/cash equivalents at the year end:', type: 'bold-total', path: 'yearEndCash', format: 'currency' },
  ],
};
